/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';
import moment from 'moment';
import { nanoid } from 'nanoid';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import './shifts.scss';

import { createManualTabelRows } from '~modules/tabel/api';
import { ShiftTypes } from '~modules/tabel/interfaces/shift-types';
import { TabelRowManualCreateDto } from '~modules/tabel/model/tabel-row-manual-create.dto';
import { useLoadAvailableSections } from '~pages/cabinet/admin/sections/SectionList/use-load-available-sectionds';
import { config } from '~shared/config';
import { routerKeys } from '~shared/router';
import { toastError, toastErrorMultiline, toastSuccess } from '~shared/ui';
import { AddingBtn } from '~shared/ui/button/adding-btn';
import { SaveBtn } from '~shared/ui/button/save-btn';

import { IShiftItem } from './interface';
import { ShiftItem } from './shift-item';

const parseEnvInterval = (str: string): { start: { hh: number; mm: number }; end: { hh: number; mm: number } } => {
	try {
		const [start, end] = str.split('-').map(interval => {
			const [hh, mm] = interval.split(':');
			return { hh: parseInt(hh), mm: parseInt(mm) };
		});

		return {
			start,
			end,
		};
	} catch (err) {
		console.error(
			`Ошибка при парсинге DAY_SHIFT_INTERVAL/NIGHT_SHIFT_INTEVAL, установлены значения по умолчанию: ${err instanceof Error ? err.message : 'Unknown error'}`,
		);
		return {
			start: {
				hh: 9,
				mm: 0,
			},
			end: {
				hh: 21,
				mm: 0,
			},
		};
	}
};

const getIntervalByShiftType = (date: Date, shiftType: ShiftTypes): { start: Date; end: Date } => {
	const interval = parseEnvInterval(
		shiftType === ShiftTypes.day ? config.dayShiftInterval : config.nightShiftInterval,
	);

	const mstart = moment(date).startOf('day');
	mstart.hours(interval.start.hh);
	mstart.minutes(interval.start.mm);
	const mend =
		interval.start.hh * 60 + interval.start.mm >= interval.end.hh * 60 + interval.end.mm
			? moment(date).add(1, 'days').startOf('day')
			: moment(date).startOf('day');
	mend.hours(interval.end.hh);
	mend.minutes(interval.end.mm);

	return {
		start: mstart.toDate(),
		end: mend.toDate(),
	};
};

export function Shifts() {
	const navigate = useNavigate();
	const { list: sections } = useLoadAvailableSections();
	const [shifts, setShifts] = useState<IShiftItem[]>([]);

	const handleShiftItem = useCallback((changedRow: IShiftItem) => {
		const { start, end } = getIntervalByShiftType(changedRow.date, changedRow.shiftType);

		setShifts(state =>
			state.map(v => {
				if (v.idx !== changedRow.idx) return v;

				if (v.shiftType !== changedRow.shiftType || v.date !== changedRow.date) {
					return { ...changedRow, scheduledStart: start, scheduledEnd: end };
				}

				return changedRow;
			}),
		);
	}, []);

	const handleAddEmptyShift = useCallback(() => {
		try {
			shifts.forEach(validateShift);
			setShifts(state => [
				...state,
				{
					idx: nanoid(),
					date: new Date(),
					users: [],
					scheduledStart: null,
					scheduledEnd: null,
					shiftType: '' as any,
					workTimeType: '' as any,
					section_id: 0,
				},
			]);
		} catch (err) {
			if (err instanceof Error) {
				toastError(err.message);
			} else console.error(err);
		}
	}, [shifts]);

	const handleDeleteItem = useCallback((shiftItemIdx: string) => {
		setShifts(state => state.filter(value => value.idx !== shiftItemIdx));
	}, []);

	const renderShiftItem = useCallback(
		(shift: IShiftItem) => (
			<ShiftItem shiftItem={shift} onChange={handleShiftItem} onDelete={handleDeleteItem} sections={sections} />
		),
		[handleDeleteItem, handleShiftItem, sections],
	);

	const validateShift = (shift: IShiftItem): boolean => {
		if (!shift.shiftType) throw new Error('Вы не указали смену');
		if (!shift.date) throw new Error('Вы не указали дату');
		if (!shift.users || shift.users.length === 0) throw new Error('Вы не указали пользователей');
		if (!shift.workTimeType) throw new Error('Вы не указали тип рабочего времени');
		if (shift.section_id === null || shift.section_id === undefined) throw new Error('Вы не указали участок');
		return true;
	};

	const handleSave = useCallback(async () => {
		try {
			shifts.forEach(validateShift);

			const tabelRows = shifts.reduce((acc, curr) => {
				const rows: TabelRowManualCreateDto[] = curr.users.map(user => ({
					user_id: user.id!,
					shift_type: curr.shiftType,
					work_time_type: curr.workTimeType,
					date: curr.date,
					start: curr.scheduledStart,
					end: curr.scheduledEnd,
					section_id: curr.section_id,
				}));
				acc.push(...rows);
				return acc;
			}, [] as TabelRowManualCreateDto[]);

			await createManualTabelRows(tabelRows);
			setShifts([]);
			toastSuccess('Смены добавлены');
			navigate(routerKeys.tabel.root());
		} catch (err) {
			if (axios.isAxiosError(err)) {
				if (err.response?.data?.message) {
					toastErrorMultiline(`${err.response?.data?.message}`);
				} else {
					toastError('Неизвестная ошибка');
				}
			} else if (err instanceof Error) {
				toastError(err.message);
			} else {
				console.error(err);
			}
		}
	}, [navigate, shifts]);

	return (
		<div className="shifts-page page">
			<div className="page-container">
				<h2 className="page-title" style={{ marginBottom: '40px' }}>
					Смены
				</h2>
				<table className="table-for-tabel">
					<thead>
						<tr>
							<th>Смена</th>
							<th style={{ width: '150px' }}>Дата начала</th>
							<th style={{ width: '150px' }}>Расписание</th>
							<th style={{ width: '350px' }}>Сотрудники</th>
							<th>Вид работ</th>
							<th>Участок</th>
						</tr>
					</thead>
					<tbody>
						{shifts.map(item => (
							<tr key={item.idx}>{renderShiftItem(item)}</tr>
						))}
						<tr>
							<td colSpan={6}>
								<AddingBtn onClick={handleAddEmptyShift} />
							</td>
						</tr>
					</tbody>
				</table>
				<div style={{ display: 'flex', justifyContent: 'end', alignItems: 'center' }}>
					<SaveBtn disabled={shifts.length === 0} style={{ margin: '20px auto 0' }} handleSave={handleSave} />
				</div>
			</div>
		</div>
	);
}
